import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import SomethingHappened from '@/components/SomethingHappened';
import { useCageTimezone } from '@/contexts/cage';
import {
    type NewReport,
    useCreateHarvestReport,
    useDeleteHarvestReport,
    useGetHarvestReports,
} from '@/domain/harvestReports';
import useGoToUrl from '@/hooks/useGoToUrl';
import useInterval from '@/hooks/useInterval';
import batchServiceStatus from '@/utils/batchServiceStatus';
import { toDate } from '@/utils/date';

import HarvestReportList, { type HarvestReport } from './HarvestReportList';

const REPORT_REFRESH_INTERVAL = 2000;

export interface WeightUnit {
    id: number;
    tag: string;
}

interface HarvestReportPageContainerProps {
    cageId: number;
}

export default function HarvestReportPageContainer(props: HarvestReportPageContainerProps) {
    const { cageId } = props;

    const [newReportError, setNewReportError] = useState<boolean>(false);
    const [showDraft, setShowDraft] = useState(false);

    const { t } = useTranslation(['harvestReport']);
    const timezone = useCageTimezone();
    const goToUrl = useGoToUrl();

    const {
        data: reportsData,
        isLoading,
        error,
        refetch: refetchList,
    } = useGetHarvestReports(cageId);

    const { mutateAsync: createReport } = useCreateHarvestReport(cageId, {
        onSuccess: () => refetchList(),
    });
    // `mutate` reports a failure through onError instead of rejecting, as nothing awaits the delete.
    const { mutate: deleteReport } = useDeleteHarvestReport(cageId, {
        onSuccess: () => refetchList(),
        onError: (deleteError) =>
            ToastError(
                apiErrorContent(deleteError, t('harvestReport:reportList.deleteFailed')),
                'harvest-report-delete-error'
            ),
    });

    const reports: HarvestReport[] =
        (reportsData ?? []).map((item) => {
            const { status, updatedAt, lastFeedDay, slaughterDay, draft, ...report } = item;
            return {
                ...report,
                isDraft: draft ?? false,
                // The zone only gates the time: an instant is the same Date in every zone.
                updatedAt: updatedAt && timezone ? toDate(updatedAt) : null,
                lastFeedDay: lastFeedDay ? toDate(lastFeedDay) : null,
                slaughterDay: slaughterDay ? toDate(slaughterDay) : null,
                status: batchServiceStatus({ status, updatedAt: new Date(updatedAt) })?.status,
                onDelete: () => {
                    deleteReport(report.id);
                },
                onClick: () => {
                    (status === 'finished' || status === 'warning') && goToUrl(`/${report.id}`);
                },
            };
        }) ?? [];

    const anyReportIsRunning = reports.some(
        ({ status }) => status === 'started' || status === 'created'
    );

    const hasDrafts = reports.some((r) => r.isDraft);
    const filteredReports = showDraft ? reports : reports.filter((r) => !r.isDraft);

    useInterval(refetchList, anyReportIsRunning ? REPORT_REFRESH_INTERVAL : null);

    const onCreateReport = async (requestBody: NewReport) => {
        setNewReportError(false);
        try {
            await createReport(requestBody);
            analytics.reportCreated({ reportType: 'harvest' });
        } catch (_e: unknown) {
            setNewReportError(true);
        }
    };

    return (
        <>
            {(error || newReportError) && <SomethingHappened />}
            <HarvestReportList
                isLoading={isLoading}
                reports={filteredReports}
                hasDrafts={hasDrafts}
                showDraft={showDraft}
                onToggleShowDraft={() => setShowDraft((prev) => !prev)}
                onCreateReport={onCreateReport}
            />
        </>
    );
}
